import type { FC } from 'react';
import LoadingOverlay from '@components/LoadingOverlay';
import { Show } from '@components/Show';
import { EDITING_BLOCK_Z_INDEX } from '@constants/z-indices';
import { ctxIsPDF } from '@ContractBuilder/rules/block/is-ctx';
import { type DocumentContext } from '@ContractBuilder/utils';
import type { ResourceBlock } from '@domain/schemas/contract.schema';
import type { EntityData, ResourceName } from '@domain/types/entity';
import { InlineAttachmentController } from '@features/attachments';
import { isBlockOrInlineAttachment } from '@features/attachments/utils/is-inline-attachment-or-block';
import { BlockCreatorController } from '@features/block-creator';
import type { BlockSegment } from '@features/page-split';
import { useBlockFlagsGenerator } from '@hooks/use-block-flags-generator';
import { isEndorsement } from '@root/helpers';
import { isExcludedFromEndorsementPreview } from '@root/helpers/blocks';
import { replaceUnknownMentionVariables } from '@root/helpers/pdf';
import { isEndorsementViewPath } from '@utils/app-paths';
import clsx from 'clsx';

import { useEntityStore } from '../../../store';
import { leftBorderClasses } from '../classes';
import { BlockAlert } from '../components/BlockAlert';
import type { BlockFlagsContextType } from '../context/context';
import { BlockFlagsProvider } from '../context/context';
import { useMentionValues } from '../hooks/useMentionValues';
import { handleReplaceMentionValues } from '../utils/handle-replace-mention-values';
import { BlockView } from '../view/BlockView';
import { FeatureIndicators } from '../view/FeatureIndicators';

export interface BlockControllerProps {
  block: ResourceBlock;
  context: DocumentContext;
  disableControls?: boolean;
  endorsementParent?: EntityData;
  isHovered?: boolean;
  onHoverChange?: (blockId: string | undefined) => void;
  order?: number;
  schemaTreeForPdf?: any;
  sectionId?: string;
  segment?: BlockSegment;
  submission?: EntityData;
}

export const BlockController: FC<BlockControllerProps> = ({
  block,
  context,
  disableControls = false,
  endorsementParent,
  isHovered,
  onHoverChange,
  order = 0,
  schemaTreeForPdf,
  sectionId,
  segment,
  /** Pass down the submission as a prop rather than pulling it from the store to make it work with PDF render **/
  submission,
}) => {
  'use memo';

  const isPDFRender = ctxIsPDF(context);

  const { id, section_id } = block;

  const isEndorsementView = isEndorsementViewPath();

  const flags = useBlockFlagsGenerator(block, {
    context,
    disableControls,
    isEndorsementView,
  });

  const {
    details: {
      isParentEntityEditable,
      hasVariationsConditionalLogic,
      hasBeenMovedByRenewal,
      isEditable,
      isEditing,
      isVariationActionRequired,
      isVisible,
      renderPageBreak,
      showBlockAlerts,
      showCreator,
      showLockedIndicator,
      showPermissionsSetIndicator,
      showUsedInTemplatesControls,
      showVariationsIndicator,
      showVisibilityReasonsIndicatorIcon,
      variations,
      visibilityDetails,
    },
  } = flags;

  const { isHighlighted, isLoading } = useEntityStore(({ loadingBlockId, highlightedBlockId }) => ({
    isHighlighted: id === highlightedBlockId,
    isLoading: id === loadingBlockId,
  }));

  const mentionValues = useMentionValues({ block, submission, endorsementParent, schemaTreeForPdf });

  if (isPDFRender && block?.content) {
    block.content = handleReplaceMentionValues(block, mentionValues) ?? block.content;
    block.content = replaceUnknownMentionVariables(block.content);
  }

  const value: BlockFlagsContextType = { ...flags, block, isHighlighted, isLoading, mentionValues, context, segment };
  const isFirstSegment = segment?.isFirst ?? true;

  if (isPDFRender && !isVisible && !isEndorsement(submission?.pk as ResourceName)) {
    return null;
  }

  const isHiddenFromPDFPreview = isExcludedFromEndorsementPreview(block, submission);

  return (
    <BlockFlagsProvider value={value}>
      <div
        className={clsx(
          'group relative',
          isEditing && EDITING_BLOCK_Z_INDEX,
          isEndorsementView && 'remove-interactive-mention',
        )}
        data-test-block-id={block.id}
        data-hovered={isHovered || undefined}
        onMouseEnter={() => onHoverChange?.(id)}
        onMouseLeave={() => onHoverChange?.(undefined)}
        {...(isParentEntityEditable ? { 'data-editable': true } : {})}
      >
        <Show when={!isPDFRender}>
          <div
            id="left-border"
            className={leftBorderClasses({
              editing: isEditing,
              editable: isParentEntityEditable,
              disableHoverEffect: !isEditable,
              forceHoverEffect: isVariationActionRequired,
            })}
          />
          <LoadingOverlay active={isLoading} size="sm" />
        </Show>
        {isBlockOrInlineAttachment(block) === 'block' && <BlockView sectionId={sectionId} submission={submission} />}
        {isBlockOrInlineAttachment(block) === 'attachment' && <InlineAttachmentController />}
        <FeatureIndicators
          block={block}
          hasVariationsConditionalLogic={hasVariationsConditionalLogic}
          isEnabled={!isPDFRender && !isEditing && isFirstSegment}
          isHiddenFromPDFPreview={isHiddenFromPDFPreview}
          isVisible={isVisible}
          showLockedIndicator={showLockedIndicator}
          showPermissionsSetIndicator={showPermissionsSetIndicator}
          showUsedInTemplatesControls={showUsedInTemplatesControls}
          showVariationsIndicator={showVariationsIndicator}
          showVisibilityReasonsIndicatorIcon={showVisibilityReasonsIndicatorIcon}
          variations={variations}
          visibilityDetails={visibilityDetails}
        />
        <Show when={showBlockAlerts && isFirstSegment}>
          <BlockAlert block={block} hasBeenMovedByRenewal={hasBeenMovedByRenewal} />
        </Show>
        <Show when={showCreator && (segment?.withCreator ?? true)}>
          <BlockCreatorController id={id} order={order} sectionId={section_id} />
        </Show>
        <Show when={renderPageBreak}>
          <hr />
        </Show>
      </div>
    </BlockFlagsProvider>
  );
};
